import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Input, Select, DatePicker, InputNumber, Divider, Row, Col, App } from 'antd';
import moment from 'moment';
import dayjs from "dayjs";
import { ImmoService } from '../Services/ImmoService';

const { Option } = Select;

const ImmobilisationModal = ({ isVisible, setIsVisible, immobilisation, onSave }) => {

  const [form] = Form.useForm();
  const [familleImmo, setFamilleImmo] = useState([]);
  const [comptesImmo, setComptesImmo] = useState([]);
  const [comptesAmort, setComptesAmort] = useState([]);
  const [comptesDotation, setComptesDotation] = useState([]);
  const immoService = new ImmoService();


  const prefixesImmo = ['212', '213', '231', '238', '244', '245'];
  const prefixesAmort = ['281', '282', '283', '284'];
  const prefixesDot = ['680', '681'];


  useEffect(() => {
    if (!isVisible) return;

    const fetchData = async () => {
      try {
        const familleData = await immoService.getListImmoFam();
        setFamilleImmo(familleData);

        const response = await immoService.getListImmoPlan();
        const comptesImmo = [];
        const comptesAmort = [];
        const comptesDotation = [];

        response.forEach(compte => {
          if (prefixesImmo.some(prefix => compte.compte.startsWith(prefix))) {
            comptesImmo.push(compte);
          } else if (prefixesAmort.some(prefix => compte.compte.startsWith(prefix))) {
            comptesAmort.push(compte);
          } else if (prefixesDot.some(prefix => compte.compte.startsWith(prefix))) {
            comptesDotation.push(compte);
          }
        });

        setComptesImmo(comptesImmo);
        setComptesAmort(comptesAmort);
        setComptesDotation(comptesDotation);
      } catch (error) {
        console.error('Erreur lors de la récupération des données :', error);
      }
    };

    fetchData();

    if (immobilisation) {
      form.setFieldsValue({ ...immobilisation });
    }
  }, [isVisible, immobilisation, form]);




  const handleOk = () => {
    form.validateFields().then(values => {
      const dataToSave = {
        ...values,
        val_acquis: values.val_acquis.toString(),
        perim: "IPM",
        _compte_immo: comptesImmo.find(c => c.compte === values.compte_immo),
        _compte_amort: comptesAmort.find(c => c.compte === values.compte_amort),
        _compte_dot: comptesDotation.find(c => c.compte === values.compte_dot),
        _famille: {
          code_fam_immo: values.famille,
          lib_fam_immo: familleImmo.find(item => item.code_fam_immo === values.famille).lib_fam_immo
        },
        _mode: {
          code: values.mode,
          libelle: (values.mode === "LIN" ? "Linéaire" : "Dégressif")
        },
        _depart: {
          code: values.depart,
          libelle: (values.depart === "DMS" ? "Date mise en service" : "Date acquisition")
        }
      };

      onSave(dataToSave);
      setIsVisible(false);
    }).catch(info => {
      console.log('Validate Failed:', info);
    });
  };


  const handleCancel = () => {
    setIsVisible(false);
    form.resetFields();
  };

  React.useEffect(() => {
    if (immobilisation && isVisible) {
      form.setFieldsValue({ ...immobilisation });
    }
  }, [immobilisation, isVisible, form]);


  return (
    <Modal
      width={800}
      style={{ top: 20 }}
      title={immobilisation ? 'Modification Immobilisation' : 'Création Immobilisation'}
      open={isVisible}
      onOk={handleOk}
      onCancel={handleCancel}
      maskClosable={false}
      footer={[
        <Button key="back" onClick={handleCancel}>
          Annuler
        </Button>,
        <Button key="submit" type="primary" onClick={handleOk}>
          {immobilisation ? 'Modifier' : 'Créer'}
        </Button>,
      ]}
    >

      <Form form={form} layout="vertical">
        <Divider orientation="left">Identification</Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={8}>
            <Form.Item label="Code" name="code" rules={[{ required: true, message: 'Veuillez saisir le code!' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} xl={8}>
            <Form.Item label="Désignation" name="designation" rules={[{ required: true, message: 'Veuillez saisir la désignation!' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} xl={8}>
            <Form.Item label="Famille" name="famille">
              <Select>
                {familleImmo.map((famille) => (
                  <Option key={famille.code_fam_immo} value={famille.code_fam_immo}>
                    {`${famille.lib_fam_immo} `}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} xl={8}>
            <Form.Item label="Date d'acquisition" name="date_acquis">
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>

          </Col>
          <Col xs={24} xl={8}>
            <Form.Item label="Valeur d'acquisition" name="val_acquis">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col xs={24} xl={8}>
            <Form.Item label="Date mise en service" name="date_mes">
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left">Amortissement</Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={4}>
            <Form.Item label="Durée (ans)" name="duree_amort">
              <InputNumber defaultValue="1" style={{ width: "100%" }} min={1} />
            </Form.Item>
          </Col>
          <Col xs={24} xl={4}>
            <Form.Item label="Mode" name="mode">
              <Select defaultValue="LIN">
                <Option value="LIN">Linéaire</Option>
                <Option value="DEG">dégressif</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} xl={8}>
            <Form.Item label="Départ" name="depart">
              <Select defaultValue="DMS">
                <Option value="DMS">Date mise en service</Option>
                <Option value="DAC">Date acquisition</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} xl={6}>
            <Form.Item label="Prorata" name="prorata" >
              <Select defaultValue="mois">
                <Option value="mois">Mois</Option>
                <Option value="jours">Jour</Option>
              </Select>
            </Form.Item>
          </Col>

        </Row>
        <Form.Item label="Lieu" name="lieu">
          <Input />
        </Form.Item>
        <Divider orientation="left">Comptabilité</Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <Form.Item label="Compte Immobilisation" name="compte_immo">
              <Select>
                {comptesImmo.map(comptePLan => (
                  <Option key={comptePLan.compte} value={comptePLan.compte}>{comptePLan.compte} - {comptePLan.intitule}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} xl={12}>
            <Form.Item label="Compte Amortissement" name="compte_amort">
              <Select>
                {comptesAmort.map(comptePLan => (
                  <Option key={comptePLan.compte} value={comptePLan.compte}>{comptePLan.compte} - {comptePLan.intitule}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <Form.Item label="Compte Dotation" name="compte_dot">

              <Select>
                {comptesDotation.map(comptePLan => (
                  <Option key={comptePLan.compte} value={comptePLan.compte}>{comptePLan.compte} - {comptePLan.intitule}</Option>
                ))}
              </Select>

            </Form.Item>
          </Col>


        </Row>
      </Form>

    </Modal>
  );
};

export default ImmobilisationModal;
